/**
 * A/B tests of the website UI and emails. Pure and browser-safe; the tests
 * themselves are written in code (utils/ui-tests/registry.ts), the store that
 * runs them is utils/ui-tests/store.ts, and the plan is docs/UI_TESTS_PLAN.md.
 *
 * A test is a registry entry: its arms and what each renders live in code. A
 * RUN is one go at it, started from the dash, with a split of people over the
 * arms. Assignment is a hash of the run id and the browser's anonymous id into
 * a hundred buckets, so a restarted test reshuffles people and two tests split
 * them independently of each other.
 *
 * A run is `running` (arms split), then `promoted` (one arm for everyone, and
 * still measured, so its lift can be checked after the fact) or `ended`.
 * An ended run that was promoted keeps serving its arm, so ending the
 * tracking never flips the site back to control.
 */
import { z } from 'zod';
import { ExperimentVerdictRecordSchema } from './experiment-verdict';

export const UI_TEST_BUCKETS = 100;
export const UI_CONTROL = 'control';

export const UiTestIdSchema = z.string().regex(/^[a-z0-9][a-z0-9-]{0,38}[a-z0-9]$/, 'lowercase letters, digits and dashes, 2-40');
export const UiArmIdSchema = z.string().regex(/^[a-z0-9][a-z0-9-]{0,23}$/, 'lowercase letters, digits and dashes, up to 24');
/** Any well-formed id; utils/ui-tests/registry.ts has the literal ids of the tests that exist. */
export type UiTestIdString = z.infer<typeof UiTestIdSchema>;
export type UiArmIdString = z.infer<typeof UiArmIdSchema>;

/**
 * What a test can be judged on. One shared list, so the dash, the results and
 * the after-promotion check read a goal the same way. See utils/ui-tests/results.ts.
 */
export const UI_TEST_GOALS = ['clickThrough', 'viewsPerSession', 'saveRate', 'checkoutDepth', 'ordersPerPerson', 'returnRate', 'emailClickRate'] as const;
export const UiTestGoalSchema = z.enum(UI_TEST_GOALS);
export type UiTestGoal = z.infer<typeof UiTestGoalSchema>;

/** Percent of people per arm, control included. Whole percents that add up to 100. */
export const UiTestSplitSchema = z.record(UiArmIdSchema, z.number().int().min(0).max(UI_TEST_BUCKETS));
export type UiTestSplit = z.infer<typeof UiTestSplitSchema>;

export const UiTestRunSchema = z.object({
  /** Unique across all tests and time: results and the bucket hash are keyed by it. */
  run: z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/),
  test: UiTestIdSchema,
  status: z.enum(['running', 'promoted', 'ended']),
  split: UiTestSplitSchema,
  startedAt: z.string(),
  startedBy: z.string().nullable(),
  promotedArm: UiArmIdSchema.nullable().default(null),
  promotedAt: z.string().nullable().default(null),
  promotedBy: z.string().nullable().default(null),
  endedAt: z.string().nullable().default(null),
  endedBy: z.string().nullable().default(null),
  note: z.string().max(500).nullable().default(null),
  /** Promote a clear winner without asking (set at start). Off: the cron mails and a person promotes. */
  autoPromote: z.boolean().default(false),
  verdict: ExperimentVerdictRecordSchema.nullable().default(null),
});
export type UiTestRun = z.infer<typeof UiTestRunSchema>;

export const StoredUiTestsSchema = z.object({
  /** The latest run of each test, by test id, whatever its status. */
  current: z.record(UiTestIdSchema, UiTestRunSchema).default({}),
  /** Earlier runs, newest first. */
  log: z.array(UiTestRunSchema).default([]),
  version: z.number().int().min(0).default(0),
  updatedAt: z.string().nullable().default(null),
});
export type StoredUiTests = z.infer<typeof StoredUiTestsSchema>;

/**
 * What one test serves one browser.
 *
 * `record` says whether showing it counts: true while a run is running or
 * promoted and the browser has an id the results can follow. `phase` tells a
 * test exposure from an after-promotion one.
 */
export const ServedUiTestSchema = z.object({
  arm: UiArmIdSchema,
  run: z.string(),
  phase: z.enum(['test', 'shipped']),
  record: z.boolean(),
});
export type ServedUiTest = z.infer<typeof ServedUiTestSchema>;

/** Every test's serving for one browser, as the `sw_ui` cookie carries it (utils/ui-tests/cookie.ts). Absent is control, not recorded. */
export const UiTestArmsResponseSchema = z.object({
  version: z.number().int().min(0),
  tests: z.record(UiTestIdSchema, ServedUiTestSchema),
});
export type UiTestArmsResponse = z.infer<typeof UiTestArmsResponseSchema>;

export const UiTestActionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('start'), test: UiTestIdSchema, split: UiTestSplitSchema, autoPromote: z.boolean().optional() }),
  z.object({ action: z.literal('stop'), test: UiTestIdSchema, note: z.string().max(500).optional() }),
  z.object({ action: z.literal('promote'), test: UiTestIdSchema, arm: UiArmIdSchema, note: z.string().max(500).optional() }),
  z.object({ action: z.literal('end-tracking'), test: UiTestIdSchema, note: z.string().max(500).optional() }),
]);
export type UiTestAction = z.infer<typeof UiTestActionSchema>;

/** The config document the runs live in (Elasticsearch config index); history copies add `-history-<version>`. */
export const UI_TESTS_DOC_ID = 'ui-tests';

/** The exposure row's event type in ClickHouse `events`; its `properties` carry test, run, arm and phase. */
export const UI_TEST_EXPOSURE_EVENT = 'ui_test_exposure';
